import type { Rosie } from "../entities/Rosie";
import type { DustBunny } from "../entities/DustBunny";
import { dist2, shortestAngleDiff } from "../types";
import { WORLD_RADIUS } from "../World";

export type BotPersonality = "gatherer" | "hunter";

/**
 * Steering AI for an opponent Rosie.
 *
 * Two personalities:
 *   - `gatherer` (default): hunts dust, avoids trails, curves away from walls.
 *   - `hunter`: same baseline, but periodically commits to a *chase* — aiming
 *     at the player's predicted future position 1 second ahead — and during
 *     a chase becomes less cautious about trail proximity (more deadly to the
 *     player, more crashable for the bot — risk/reward).
 *
 * Both personalities share the same loop-breaker: an exp-decaying signed turn
 * integral that forces a "drive straight" override if the bot has circled.
 */
export class BotBrain {
  personality: BotPersonality;

  private readonly dustReach = 600;
  private readonly baseAvoidReach = 90;
  private readonly chaseAvoidReach = 55; // hunters in chase mode brave the trails more
  private readonly edgeReach = 240;
  private readonly ownTrailNeckSkip = 28;
  private readonly lookahead = 80;

  private cumulativeTurn = 0;
  private prevHeading = Number.NaN;
  private straightenUntil = 0;
  /** Wall-clock ms during which a hunter commits to chasing the player. */
  private chaseUntil = 0;

  constructor(personality: BotPersonality = "gatherer") {
    this.personality = personality;
  }

  think(
    self: Rosie,
    others: Rosie[],
    dust: DustBunny[],
    dt: number,
  ): void {
    const now = performance.now();

    // Turn-integral tracking (exp decay) → loop breaker.
    if (!Number.isNaN(this.prevHeading)) {
      const signed = shortestAngleDiff(this.prevHeading, self.heading);
      this.cumulativeTurn =
        this.cumulativeTurn * Math.exp(-dt / 3) + signed;
    }
    this.prevHeading = self.heading;
    if (
      Math.abs(this.cumulativeTurn) > Math.PI * 2.2 &&
      now > this.straightenUntil
    ) {
      this.straightenUntil = now + 1200;
      this.cumulativeTurn = 0;
    }
    const isStraightening = now < this.straightenUntil;

    // Hunter: decide whether to commit to a chase this frame.
    const player = others.find((r) => r.isPlayer && r.alive);
    if (
      this.personality === "hunter" &&
      player &&
      now > this.chaseUntil &&
      !isStraightening
    ) {
      const dx = player.x - self.x;
      const dy = player.y - self.y;
      const dist = Math.hypot(dx, dy);
      // Roll once per frame. ~50% per second to commit when in range.
      // When the player is in Robot Mode we used to drop this to 8% — that
      // turned every 5-second invincibility window into a free farm. We
      // still discount it (no point ramming an immune target with our own
      // head), but at 25% the hunters keep the pressure on so the player
      // has to keep weaving, not just float through the arena.
      const playerImmune = player.invincibleUntil > now;
      const chanceRate = (playerImmune ? 0.25 : 0.5) * (dist < 700 ? 1 : 0);
      if (Math.random() < chanceRate * dt) {
        this.chaseUntil = now + 1500 + Math.random() * 1500;
      }
    }
    const isChasing =
      this.personality === "hunter" &&
      now < this.chaseUntil &&
      player !== undefined &&
      player.alive;

    let targetHeading: number;
    let confidence = 0;

    if (isStraightening) {
      // Force-straighten override — only emergency avoidance below can budge us.
      targetHeading = self.heading;
      confidence = 0.4;
    } else if (isChasing && player) {
      // Aim at where the player will be ~1s from now.
      const leadTime = 1.0;
      const futureX = player.x + Math.cos(player.heading) * player.speed * leadTime;
      const futureY = player.y + Math.sin(player.heading) * player.speed * leadTime;
      targetHeading = Math.atan2(futureY - self.y, futureX - self.x);
      confidence = 0.85;
    } else {
      // Default: dust by distance + angle cost.
      let bestDust: DustBunny | null = null;
      let bestScore = Infinity;
      for (const d of dust) {
        const dx = d.x - self.x;
        const dy = d.y - self.y;
        const dd = dx * dx + dy * dy;
        if (dd > this.dustReach * this.dustReach) continue;
        const distance = Math.sqrt(dd);
        const angle = Math.atan2(dy, dx);
        const turnCost = Math.abs(shortestAngleDiff(self.heading, angle));
        const score = distance + turnCost * 250 + d.value * -30;
        if (score < bestScore) {
          bestScore = score;
          bestDust = d;
        }
      }
      if (bestDust) {
        targetHeading = Math.atan2(bestDust.y - self.y, bestDust.x - self.x);
        confidence = 0.7;
      } else {
        targetHeading = Math.atan2(-self.y, -self.x);
        confidence = 0.3;
      }
    }

    // Trail repulsion. Chasers have a tighter reach — they accept more risk.
    const avoidReach = isChasing ? this.chaseAvoidReach : this.baseAvoidReach;
    let avoidVecX = 0;
    let avoidVecY = 0;
    let avoidStrength = 0;
    const aheadX = self.x + Math.cos(self.heading) * this.lookahead;
    const aheadY = self.y + Math.sin(self.heading) * this.lookahead;
    const all = [self, ...others];
    for (const other of all) {
      if (!other.alive) continue;
      const trail = other.trail;
      const start = other === self ? this.ownTrailNeckSkip : 0;
      for (let i = start; i < trail.length; i += 3) {
        const p = trail[i];
        const dd = dist2(aheadX, aheadY, p.x, p.y);
        if (dd < avoidReach * avoidReach) {
          const d = Math.sqrt(dd);
          const w = 1 - d / avoidReach;
          avoidVecX += (aheadX - p.x) * w;
          avoidVecY += (aheadY - p.y) * w;
          avoidStrength = Math.max(avoidStrength, w);
        }
      }
    }
    if (avoidStrength > 0) {
      const avoidHeading = Math.atan2(avoidVecY, avoidVecX);
      targetHeading = blendAngles(targetHeading, avoidHeading, avoidStrength);
      confidence = Math.max(confidence, avoidStrength);
    }

    // Arena edge.
    const distFromCenter = Math.hypot(self.x, self.y);
    if (distFromCenter > WORLD_RADIUS - this.edgeReach) {
      const inward = Math.atan2(-self.y, -self.x);
      const w =
        (distFromCenter - (WORLD_RADIUS - this.edgeReach)) / this.edgeReach;
      targetHeading = blendAngles(targetHeading, inward, Math.min(1, w * 1.5));
      confidence = 1;
    }

    const cur = self.desiredHeading;
    const diff = shortestAngleDiff(cur, targetHeading);
    self.desiredHeading = cur + diff * Math.min(1, dt * (2 + confidence * 6));
  }
}

function blendAngles(a: number, b: number, t: number): number {
  const diff = shortestAngleDiff(a, b);
  return a + diff * t;
}
